import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ServiceItem } from "../types";
import { Add } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { ServiceItemRow } from "@/pages/settings/pages/products-v2/components/ServiceItemRow";
import { cn } from "@/utils/utils";

const TRANSLATION_KEY = "settings.product";

interface ServiceItemTableProps {
  serviceItems: ServiceItem[];
  onDragEnd: (result: any) => void;
  onEditServiceItem?: (serviceItem: ServiceItem) => void;
  onDeleteServiceItem?: (serviceItem: ServiceItem) => void;
  onAddServiceItem?: () => void;
  /** Off when the table is the first thing inside its own bordered card. */
  withTopBorder?: boolean;
}

export const ServiceItemTable: React.FC<ServiceItemTableProps> = ({
  serviceItems,
  onDragEnd,
  onEditServiceItem,
  onDeleteServiceItem,
  onAddServiceItem,
  withTopBorder = true
}) => {
  const { t } = useTranslation();

  if (serviceItems.length === 0 && onAddServiceItem) {
    return (
      <div
        className={cn(
          "text-center py-8 border-border-border1",
          withTopBorder && "border-t"
        )}
        data-test="products-service-table-empty"
      >
        <Text variant="body" className="mb-2">
          {t(`${TRANSLATION_KEY}.serviceItemTable.noServiceItems`)}
        </Text>
        <Text variant="bodySecondary" className="mb-4">
          {t(`${TRANSLATION_KEY}.serviceItemTable.noServiceItemsSubtitle`)}
        </Text>
        <Button
          variant="tertiary"
          onClick={onAddServiceItem}
          data-test="products-service-table-empty-add-button"
        >
          <IconContainer color="cta" size="sm">
            <Add />
          </IconContainer>
          {t(`${TRANSLATION_KEY}.serviceItemTable.addServiceItem`)}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-test="products-service-table">
      <div
        className={cn(
          "flex items-center min-h-11 px-4 py-2 bg-transparent border-b border-border-border1",
          withTopBorder && "border-t"
        )}
        data-test="products-service-table-header"
      >
        <div className="w-6"></div>
        <div className="w-[180px]">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY}.serviceItemTable.serviceName`)}
          </Text>
        </div>
        <div className="w-[180px]">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY}.serviceItemTable.type`)}
          </Text>
        </div>
        <div className="flex-1">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY}.serviceItemTable.pricing`)}
          </Text>
        </div>
        <div className="w-[72px]"></div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="service-items-table">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {serviceItems.map((serviceItem, index) => (
                <Draggable
                  key={serviceItem.id}
                  draggableId={serviceItem.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <ServiceItemRow
                      serviceItem={serviceItem}
                      provided={provided}
                      snapshot={snapshot}
                      onEditServiceItem={onEditServiceItem}
                      onDeleteServiceItem={onDeleteServiceItem}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {onAddServiceItem && (
        <div className="p-2 ml-4">
          <Button
            variant="tertiary"
            onClick={onAddServiceItem}
            data-test="products-service-table-add-button"
          >
            <IconContainer color="cta" size="sm">
              <Add />
            </IconContainer>
            {t(`${TRANSLATION_KEY}.serviceItemTable.addService`)}
          </Button>
        </div>
      )}
    </div>
  );
};
